import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import "./FloatingCodeSnippets.css";

export type FloatingSnippet = {
  id: string;
  code: string;
  position: { x: string; y: string };
  rotate: number;
  delay: number;
  color: string;
};

type FloatingCodeSnippetsProps = {
  snippets: FloatingSnippet[];
  className?: string;
  interactive?: boolean;
};

const CodeChip: React.FC<{ snippet: FloatingSnippet; interactive?: boolean }> = ({
  snippet,
  interactive = false,
}) => {
  const [displayedCode, setDisplayedCode] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const resetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let currentIndex = 0;
    const code = snippet.code;

    const startTyping = () => {
      setDisplayedCode("");
      setIsTyping(true);
      currentIndex = 0;

      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }

      intervalRef.current = setInterval(() => {
        if (currentIndex < code.length) {
          setDisplayedCode(code.slice(0, currentIndex + 1));
          currentIndex += 1;
          return;
        }

        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }

        setIsTyping(false);

        resetTimeoutRef.current = setTimeout(() => {
          setDisplayedCode("");
          startTimeoutRef.current = setTimeout(startTyping, 1200 + Math.random() * 1800);
        }, 2200 + Math.random() * 2000);
      }, 38 + Math.random() * 24);
    };

    startTimeoutRef.current = setTimeout(startTyping, snippet.delay * 1000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (resetTimeoutRef.current) clearTimeout(resetTimeoutRef.current);
      if (startTimeoutRef.current) clearTimeout(startTimeoutRef.current);
    };
  }, [snippet.code, snippet.delay]);

  const lines = displayedCode ? displayedCode.split("\n") : [""];
  const positionStyle = {
    left: snippet.position.x,
    top: snippet.position.y,
  } as React.CSSProperties;
  const chipStyle = {
    "--snippet-color": snippet.color,
  } as React.CSSProperties;
  const chipClassName = [
    "floating-code-snippets__item",
    interactive
      ? "floating-code-snippets__item--interactive"
      : "floating-code-snippets__item--static",
  ].join(" ");

  return (
    <div className={chipClassName} style={positionStyle}>
      <motion.div
        className="floating-code-snippets__entry"
        initial={{ opacity: 0, scale: 0.85, y: 12 }}
        animate={{ opacity: 0.8, scale: 1, y: 0 }}
        transition={{ duration: 0.8, delay: snippet.delay, ease: "easeOut" }}
      >
        <motion.div
          whileHover={interactive ? { scale: 1.045 } : undefined}
          transition={{
            duration: 0.45,
            ease: [0.22, 1, 0.36, 1],
          }}
        >
          <motion.div
            className="floating-code-snippets__chip"
            style={chipStyle}
            animate={{
              y: [0, -12, 0],
              rotate: [snippet.rotate, snippet.rotate + 2, snippet.rotate],
            }}
            transition={{
              duration: 5.8,
              repeat: Infinity,
              ease: "easeInOut",
              delay: snippet.delay,
            }}
          >
            <pre className="floating-code-snippets__pre">
              <code>
                {lines.map((line, index) => (
                  <div key={`${snippet.id}-${index}`} className="floating-code-snippets__line">
                    <span className="floating-code-snippets__line-number">{index + 1}</span>
                    <span
                      className={[
                        "floating-code-snippets__line-text",
                        index === 0 ? "floating-code-snippets__line-text--accent" : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                    >
                      {line}
                      {index === lines.length - 1 ? (
                        <motion.span
                          animate={{ opacity: [1, 0, 1] }}
                          transition={{ duration: 0.8, repeat: Infinity }}
                          className={[
                            "floating-code-snippets__cursor",
                            isTyping
                              ? "floating-code-snippets__cursor--typing"
                              : "floating-code-snippets__cursor--idle",
                          ].join(" ")}
                        />
                      ) : null}
                    </span>
                  </div>
                ))}
              </code>
            </pre>
          </motion.div>
        </motion.div>
      </motion.div>
    </div>
  );
};

const FloatingCodeSnippets: React.FC<FloatingCodeSnippetsProps> = ({
  snippets,
  className = "",
  interactive = false,
}) => (
  <div
    className={["floating-code-snippets", className].filter(Boolean).join(" ")}
    aria-hidden="true"
  >
    {snippets.map((snippet) => (
      <CodeChip key={snippet.id} snippet={snippet} interactive={interactive} />
    ))}
  </div>
);

export default FloatingCodeSnippets;
